/**
 * // Class Obj: クリックイベントを管理
 */
export class ClickEventHandler
{
  public mTrigger: HTMLElement | null;
  public mTarget: HTMLElement | null;
  private readonly mEventListener: Map<HTMLElement, EventListener> = new Map<HTMLElement, EventListener>();

  /**
   * // Constructor: クリックイベントのトリガーと対象となる要素を受け取る
   *
   * @param trigger_selector トリガーとなる要素のセレクタを指定
   * @param target_selector 対象となる要素のセレクタを指定
   */
  constructor(trigger_selector: HTMLElement | null = null, target_selector: HTMLElement | null = null)
  {
    this.mTrigger = trigger_selector;
    this.mTarget = target_selector;
  }

  /**
   * // Method: 対象の要素のCSSクラスに追加するクラスを切り替える
   *
   * @param class_name 切り替えで追加するCSSクラス名
   */
  public toggleClassOnTarget(class_name: string): void
  {
    if (this.mTrigger)
    {
      this.mTrigger.addEventListener('click', () =>
      {
        this.mTrigger && this.mTrigger.classList.toggle(class_name);
        this.mTarget && this.mTarget.classList.toggle(class_name);
      });
    }
  }

  /**
   * // Method: ページトップにスクロールする
   */
  public scrollToPageTop(): void
  {
    if (this.mTrigger)
    {
      this.mTrigger.addEventListener('click', () =>
      {
        window.scrollTo({
          top: 0,
        });
      });
    }
  }

  /**
   * // Method: 複数の要素にイベントを追加する
   *
   * @param callback イベントが発生した時に実行するコールバック関数
   * @param collections 複数のHTMLCollectionを引数として受け取る
   */
  public addEventToElements(
    callback: (element: HTMLElement) => void,
    ...collections: (HTMLCollectionOf<HTMLElement> | null)[]
  ): void
  {
    // 受け取った複数のコレクションを順番に処理
    for (const collection of collections)
    {
      if (!collection) continue; // collectionがnullなら処理をスキップ
      // 各collectionの中の全てのelementを処理
      for (const element of collection)
      {
        // 既にそのelementに登録されているイベントリスナーを取得
        const existing_listener: EventListener | undefined = this.mEventListener.get(element);
        // もし既存のリスナーがあれば、それを削除
        if (existing_listener)
        {
          element.removeEventListener('click', existing_listener);
        }
        // 新しいイベントリスナーを作成
        const listener: (event: Event) => void = (event: Event) => callback(element);
        // 新しいクリックイベントリスナーを登録
        element.addEventListener('click', listener);
        // イベントリスナーをマップに格納（後で削除するため）
        this.mEventListener.set(element, listener);
      }
    }
  }

  /**
   * // Method: 複数の要素からイベントを削除する
   * @param callback イベントが発生した時に実行されたコールバック関数（削除対象）
   * @param collections 複数のHTMLCollectionを引数として受け取る
   */
  public removeEventFromElements(
    callback: (element: HTMLElement) => void,
    ...collections: (HTMLCollectionOf<HTMLElement> | null)[]
  ): void
  {
    // 受け取った複数のコレクションを順番に処理
    for (const collection of collections)
    {
      if (!collection) continue;  // collection が null の場合はスキップ
      // 各 collection の中の全ての element を処理
      for (const element of collection)
      {
        // 既にその element に登録されているイベントリスナーを取得
        const existing_listener: EventListener | undefined = this.mEventListener.get(element);
        if (existing_listener)
        {
          // もし既存のリスナーがあれば、それを削除
          element.removeEventListener('click', existing_listener);
          // さらに、リスナーをマップから削除
          this.mEventListener.delete(element);
        }
      }
    }
  }
}
